/**
 * Roles Management Hooks
 * React hooks for managing roles and permissions
 */

import { useMemo, useEffect } from 'react';
import { useMutation, useQuery } from './useBaseHooks';
import { adminApiClient } from './adminApiClient';
import { Role, Permission, RoleDTO } from './adminTypes';
import { ApiResponse, Pageable, PagedData } from './types';

/**
 * Hook to get all roles with pagination
 */
export function useRoles(
  options: {
    search?: string;
    pageable?: Pageable;
    enabled?: boolean;
    onSuccess?: (data: PagedData<Role>) => void;
    onError?: (error: Error) => void;
  } = {}
) {
  const { search, pageable, enabled = true, onSuccess, onError } = options;

  // Memoize the query function to prevent infinite re-renders
  const queryFn = useMemo(
    () => async () => {
      const params: Record<string, any> = {};
      
      if (search) {
        params.search = search;
      }
      
      if (pageable) {
        params.page = pageable.page ?? 0;
        params.size = pageable.size ?? 10;
        if (pageable.sort && pageable.sort.length > 0) {
          params.sort = pageable.sort;
        }
      } else {
        params.page = 0;
        params.size = 10;
      }

      const response = await adminApiClient.get<ApiResponse<Role[]>>(
        '/roles/list',
        params
      );

      // Handle Response wrapper - API returns { status, httpStatus, message, data: Role[] }
      if (response && response.data && Array.isArray(response.data)) {
        // Convert array to PagedData format
        const roles = response.data;
        return {
          content: roles,
          totalElements: roles.length,
          totalPages: 1,
          size: roles.length,
          number: 0,
          first: true,
          last: true,
          empty: roles.length === 0,
        };
      }

      // Handle case where response is directly the array
      if (response && Array.isArray(response)) {
        const roles = response;
        return {
          content: roles,
          totalElements: roles.length,
          totalPages: 1,
          size: roles.length,
          number: 0,
          first: true,
          last: true,
          empty: roles.length === 0,
        };
      }

      // If response.data is already PagedData
      if (response && response.data && typeof response.data === 'object' && 'content' in response.data) {
        return response.data as unknown as PagedData<Role>;
      }

      // Handle case where response itself is PagedData
      if (response && typeof response === 'object' && 'content' in response && Array.isArray(response.content)) {
        return response as PagedData<Role>;
      }
      // Fallback: return empty PagedData
      return {
        content: [],
        totalElements: 0,
        totalPages: 0,
        size: 0,
        number: 0,
        first: true,
        last: true,
        empty: true,
      };
    },
    [search, pageable?.page, pageable?.size, JSON.stringify(pageable?.sort || [])]
  );

  const query = useQuery<PagedData<Role>>(
    queryFn,
    {
      enabled,
      onSuccess: (data) => {
        onSuccess?.(data);
      },
      onError: (error) => {
        onError?.(error);
      },
    }
  );

  return query;
}

/**
 * Hook to get a single role by ID
 */
export function useRole(
  roleId: number | string,
  options: {
    enabled?: boolean;
    onSuccess?: (data: Role) => void;
    onError?: (error: Error) => void;
  } = {}
) {
  const { enabled = true, onSuccess, onError } = options;

  // Memoize the query function
  const queryFn = useMemo(
    () => async () => {
      const response = await adminApiClient.get<ApiResponse<Role>>(
        `/roles/${roleId}`
      );
      
      // Handle Response wrapper
      if (response.data) {
        return response.data;
      }
      
      return response as any;
    },
    [roleId]
  );

  const query = useQuery<Role>(
    queryFn,
    {
      enabled: enabled && !!roleId,
      onSuccess: (data) => {
        onSuccess?.(data);
      },
      onError: (error) => {
        onError?.(error);
      },
    }
  );

  return query;
}

/**
 * Hook to create a new role
 */
export function useCreateRole(
  options: {
    onSuccess?: (data: ApiResponse<Role>, variables: RoleDTO) => void;
    onError?: (error: Error, variables: RoleDTO) => void;
  } = {}
) {
  const { onSuccess, onError } = options;

  const mutation = useMutation<ApiResponse<Role>, RoleDTO>(
    async (roleData: RoleDTO) => {
      const response = await adminApiClient.post<ApiResponse<Role>>(
        '/roles',
        roleData
      );
      return response;
    },
    {
      onSuccess: (data, variables) => {
        onSuccess?.(data, variables);
      },
      onError: (error, variables) => {
        onError?.(error, variables);
      },
    }
  );

  return mutation;
}

/**
 * Hook to update an existing role
 */
export function useUpdateRole(
  options: {
    onSuccess?: (data: ApiResponse<Role>, variables: RoleDTO & { id: number }) => void;
    onError?: (error: Error, variables: RoleDTO & { id: number }) => void;
  } = {}
) {
  const { onSuccess, onError } = options;

  const mutation = useMutation<ApiResponse<Role>, RoleDTO & { id: number }>(
    async (roleData: RoleDTO & { id: number }) => {
      const { id, ...updateData } = roleData;
      const response = await adminApiClient.put<ApiResponse<Role>>(
        `/roles/${id}`,
        updateData
      );
      return response;
    },
    {
      onSuccess: (data, variables) => {
        onSuccess?.(data, variables);
      },
      onError: (error, variables) => {
        onError?.(error, variables);
      },
    }
  );

  return mutation;
}

/**
 * Hook to delete a role
 */
export function useDeleteRole(
  options: {
    onSuccess?: (data: ApiResponse<void>, variables: number) => void;
    onError?: (error: Error, variables: number) => void;
  } = {}
) {
  const { onSuccess, onError } = options;

  const mutation = useMutation<ApiResponse<void>, number>(
    async (roleId: number) => {
      const response = await adminApiClient.delete<ApiResponse<void>>(
        `/roles/${roleId}`
      );
      return response;
    },
    {
      onSuccess: (data, variables) => {
        onSuccess?.(data, variables);
      },
      onError: (error, variables) => {
        onError?.(error, variables);
      },
    }
  );

  return mutation;
}

/**
 * Hook to get all permissions
 * Note: Using hardcoded permissions for now since there's no separate permissions endpoint
 */
export function usePermissions(
  options: {
    enabled?: boolean;
    onSuccess?: (data: Permission[]) => void;
    onError?: (error: Error) => void;
  } = {}
) {
  const { enabled = true, onSuccess, onError } = options;

  // Hardcoded permissions list
  const hardcodedPermissions: Permission[] = useMemo(() => [
    {
      id: '1',
      name: 'Create User',
      resource: 'users',
      action: 'create',
      description: 'Create new users',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    },
    {
      id: '2',
      name: 'Read User',
      resource: 'users',
      action: 'read',
      description: 'View user information',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    },
    {
      id: '3',
      name: 'Update User',
      resource: 'users',
      action: 'update',
      description: 'Update user information',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    },
    {
      id: '4',
      name: 'Delete User',
      resource: 'users',
      action: 'delete',
      description: 'Delete users',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    },
    {
      id: '5',
      name: 'Create Role',
      resource: 'roles',
      action: 'create',
      description: 'Create new roles',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    },
    {
      id: '6',
      name: 'Read Role',
      resource: 'roles',
      action: 'read',
      description: 'View role information',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    },
    {
      id: '7',
      name: 'Update Role',
      resource: 'roles',
      action: 'update',
      description: 'Update role information',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    },
    {
      id: '8',
      name: 'Delete Role',
      resource: 'roles',
      action: 'delete',
      description: 'Delete roles',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    },
    {
      id: '9',
      name: 'Manage Drivers',
      resource: 'drivers',
      action: 'manage',
      description: 'Full access to driver management',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    },
    {
      id: '10',
      name: 'View Drivers',
      resource: 'drivers',
      action: 'read',
      description: 'View driver information',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    },
    {
      id: '11',
      name: 'Manage Riders',
      resource: 'riders',
      action: 'manage',
      description: 'Full access to rider management',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    },
    {
      id: '12',
      name: 'View Riders',
      resource: 'riders',
      action: 'read',
      description: 'View rider information',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    },
    {
      id: '13',
      name: 'Manage Fares',
      resource: 'fares',
      action: 'manage',
      description: 'Manage fare rates and pricing',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    },
    {
      id: '14',
      name: 'View Analytics',
      resource: 'analytics',
      action: 'read',
      description: 'View analytics and reports',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    },
    {
      id: '15',
      name: 'Manage Disputes',
      resource: 'disputes',
      action: 'manage',
      description: 'Handle and resolve disputes',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    },
    {
      id: '16',
      name: 'View Reports',
      resource: 'reports',
      action: 'read',
      description: 'View and generate reports',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    },
    {
      id: '17',
      name: 'Manage Configuration',
      resource: 'configuration',
      action: 'manage',
      description: 'Manage system configuration',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    },
    {
      id: '18',
      name: 'View Dashboard',
      resource: 'dashboard',
      action: 'read',
      description: 'View dashboard and statistics',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    },
  ], []);

  // Simulate async behavior to match the expected hook interface
  useEffect(() => {
    if (!enabled) return;

    // Simulate a small delay
    const timer = setTimeout(() => {
      onSuccess?.(hardcodedPermissions);
    }, 0);

    return () => clearTimeout(timer);
  }, [enabled, hardcodedPermissions, onSuccess]);

  return {
    data: hardcodedPermissions,
    loading: false,
    error: null,
    refetch: async () => {
      // No-op since permissions are hardcoded
    },
  };
}

